import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface AsesoriaPlan {
  id: number;
  name: string;
  price: string;
  period: string;
  description?: string;
  features: string[];
  cta: string;
  highlighted: boolean;
  sort_order: number;
}


export interface UseSupabaseAdminOptions<T> {
  table: string;
  initialData: T[];
  mapToDb?: (item: Partial<T>) => Record<string, unknown>;
  mapFromDb?: (row: Record<string, unknown>) => T;
  idColumn?: string;
}

export function useSupabaseAdmin<T extends Record<string, unknown>>(
  options: UseSupabaseAdminOptions<T>
) {
  const { table, initialData, mapToDb, mapFromDb, idColumn = 'id' } = options;
  const [data, setData] = useState<T[]>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const fromDb = useCallback(
    (row: Record<string, unknown>): T => {
      if (mapFromDb) return mapFromDb(row) as T;
      return row as T;
    },
    [mapFromDb]
  );

  const toDb = useCallback(
    (item: Partial<T>): Record<string, unknown> => {
      if (mapToDb) return mapToDb(item);
      return item as Record<string, unknown>;
    },
    [mapToDb]
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: rows, error: err } = await supabase
          .from(table)
          .select('*')
          .order(idColumn, { ascending: true });
        if (err) {
          setError(err.message);
          setData(initialData);
        } else if (rows && rows.length > 0) {
          setData(rows.map(fromDb));
        } else {
          setData(initialData);
        }
      } catch {
        setError('Error de conexión');
        setData(initialData);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [table, initialData, fromDb, idColumn]);

  const add = useCallback(
    async (item: Omit<T, 'id'> & { id?: number | string }) => {
      setLoading(true);
      try {
        const payload = toDb(item as Partial<T>);
        delete payload[idColumn];
        const { data: inserted, error: err } = await supabase
          .from(table)
          .insert(payload)
          .select()
          .single();
        if (err) {
          setError(err.message);
          return;
        }
        if (inserted) {
          const newItem = fromDb(inserted);
          setData((prev) => [...prev, newItem]);
          setHasChanges(true);
        }
      } catch {
        setError('Error al crear');
      } finally {
        setLoading(false);
      }
    },
    [table, toDb, fromDb, idColumn]
  );

  const update = useCallback(
    async (id: number | string, updates: Partial<T>) => {
      setLoading(true);
      try {
        const payload = toDb(updates);
        const { data: updated, error: err } = await supabase
          .from(table)
          .update(payload)
          .eq(idColumn, id)
          .select()
          .single();
        if (err) {
          setError(err.message);
          return;
        }
        if (updated) {
          const updatedItem = fromDb(updated);
          setData((prev) =>
            prev.map((item) =>
              (item[idColumn] === id ? updatedItem : item) as T
            )
          );
          setHasChanges(true);
        }
      } catch {
        setError('Error al actualizar');
      } finally {
        setLoading(false);
      }
    },
    [table, toDb, fromDb, idColumn]
  );

  const remove = useCallback(
    async (id: number | string) => {
      setLoading(true);
      try {
        const { error: err } = await supabase
          .from(table)
          .delete()
          .eq(idColumn, id);
        if (err) {
          setError(err.message);
          return;
        }
        setData((prev) =>
          prev.filter((item) => item[idColumn] !== id)
        );
        setHasChanges(true);
      } catch {
        setError('Error al eliminar');
      } finally {
        setLoading(false);
      }
    },
    [table, idColumn]
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from(table)
        .select('*')
        .order(idColumn, { ascending: true });
      if (err) {
        setError(err.message);
      } else if (rows) {
        setData(rows.map(fromDb));
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [table, fromDb, idColumn]);

  return { data, setData, add, update, remove, refresh, loading, error, hasChanges, setHasChanges };
}

export function useSupabaseAdminPlain<T>(table: string, initialData: T[], idColumn?: string) {
  return useSupabaseAdmin<T>({ table, initialData, idColumn });
}

export function useSupabaseAdminObject<T extends object>(
  table: string,
  keyColumn: string,
  initialData: T
) {
  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: rows, error: err } = await supabase
          .from(table)
          .select('*')
          .order('id', { ascending: true });
        if (err) {
          setError(err.message);
          setData(initialData);
          return;
        }
        if (rows && rows.length > 0) {
          const obj = { ...initialData } as Record<string, unknown>;
          rows.forEach((row: Record<string, unknown>) => {
            const k = row[keyColumn] as string;
            if (k && k in obj) obj[k] = row.value ?? row[k] ?? obj[k];
          });
          setData(obj as T);
        }
      } catch {
        setError('Error de conexión');
        setData(initialData);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [table, keyColumn, initialData]);

  const update = useCallback(
    async (updates: Partial<T>) => {
      setLoading(true);
      try {
        const entries = Object.entries(updates);
        for (const [k, v] of entries) {
          const { error: err } = await supabase
            .from(table)
            .upsert({ key: k, value: String(v) }, { onConflict: 'key' });
          if (err) {
            setError(err.message);
          }
        }
        setData((prev) => ({ ...prev, ...updates } as T));
        setHasChanges(true);
      } catch {
        setError('Error al actualizar');
      } finally {
        setLoading(false);
      }
    },
    [table]
  );

  return { data, setData, update, loading, error, hasChanges, setHasChanges };
}